import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import { AlertType, Error } from '../types/common';
import { showAlert } from './CommonSlice';
import { getCsrfToken } from '../utils/helpers';

export type ProdGroup = {
    id: string;
    name: string;
    description: string;
    url: string;
};

type ProdGroupState = {
    groups: ProdGroup[];
    selectedGroup: ProdGroup;
    isLoading: boolean;
    error: Error;
};

const BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';
const initialState: ProdGroupState = {
    groups: [],
    selectedGroup: { id: '', name: '', description: '', url: '' },
    isLoading: false,
    error: { isError: false, message: '' },
};

// ---------- Thunks ----------
export const fetchProdGroups = createAsyncThunk<ProdGroup[], void, { state: RootState }>('prodGroup/fetchProdGroups', async (_, { rejectWithValue, dispatch }) => {
    const res = await fetch(`${BASE_URL}/product-groups`, {
        method: 'GET',
        credentials: 'include',
    });
    if (!res.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: 'Не удалось загрузить группы' }));
        return rejectWithValue('fetch error');
    }
    const data = await res.json();
    return data.map((g: any) => ({ id: g._id || g.id, ...g }));
});

export const createProdGroup = createAsyncThunk('prodGroup/createProdGroup', async (group: Omit<ProdGroup, 'id'>, { rejectWithValue, dispatch }) => {
    const csrf = await getCsrfToken();
    const res = await fetch(`${BASE_URL}/product-groups`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'csrf-token': csrf },
        body: JSON.stringify(group),
    });
    if (!res.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: 'Не удалось создать группу' }));
        return rejectWithValue('create error');
    }
    const data = await res.json();
    const id = data.id || data._id;
    if (!id) {
        dispatch(showAlert({ type: AlertType.Error, message: 'Некорректный ответ сервера: отсутствует ID' }));
        return rejectWithValue('missing id');
    }
    dispatch(showAlert({ type: AlertType.Success, message: 'Группа создана' }));
    return { id, ...group } as ProdGroup;
});

export const updateProdGroup = createAsyncThunk('prodGroup/updateProdGroup', async (group: ProdGroup, { rejectWithValue, dispatch }) => {
    const csrf = await getCsrfToken();
    const res = await fetch(`${BASE_URL}/product-groups/${group.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'csrf-token': csrf },
        body: JSON.stringify(group),
    });
    if (!res.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: 'Не удалось обновить группу' }));
        return rejectWithValue('update error');
    }
    dispatch(showAlert({ type: AlertType.Success, message: 'Группа обновлена' }));
    return group;
});

export const deleteProdGroup = createAsyncThunk('prodGroup/deleteProdGroup', async (id: ProdGroup['id'], { rejectWithValue, dispatch }) => {
    const csrf = await getCsrfToken();
    const res = await fetch(`${BASE_URL}/product-groups/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'csrf-token': csrf },
    });
    if (!res.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: 'Не удалось удалить группу' }));
        return rejectWithValue('delete error');
    }
    dispatch(showAlert({ type: AlertType.Success, message: 'Группа удалена' }));
    return id;
});

// ---------- Slice ----------
export const prodGroupSlice = createSlice({
    name: 'prodGroup',
    initialState,
    reducers: {
        selectProdGroup: (state, action: PayloadAction<ProdGroup['id']>) => {
            const group = state.groups.find((g) => g.id === action.payload);
            if (group) state.selectedGroup = group;
        },
        removeSelectedProdGroup: (state) => {
            state.selectedGroup = initialState.selectedGroup;
        },
        clearError: (state) => {
            state.error = initialState.error;
        },
    },
    extraReducers: (builder) => {
        builder
            // fetch
            .addCase(fetchProdGroups.pending, (s) => {
                s.isLoading = true;
            })
            .addCase(fetchProdGroups.fulfilled, (s, { payload }) => {
                s.groups = payload;
                s.isLoading = false;
                s.error = initialState.error;
            })
            .addCase(fetchProdGroups.rejected, (s, { payload }) => {
                s.error = { isError: true, message: payload as string };
                s.isLoading = false;
            })

            // create
            .addCase(createProdGroup.pending, (s) => {
                s.isLoading = true;
            })
            .addCase(createProdGroup.fulfilled, (s, { payload }) => {
                s.groups.push(payload);
                s.isLoading = false;
                s.error = initialState.error;
            })
            .addCase(createProdGroup.rejected, (s, { payload }) => {
                s.error = { isError: true, message: payload as string };
                s.isLoading = false;
            })

            // update
            .addCase(updateProdGroup.pending, (s) => {
                s.isLoading = true;
            })
            .addCase(updateProdGroup.fulfilled, (s, { payload }) => {
                const idx = s.groups.findIndex((g) => g.id === payload.id);
                if (idx >= 0) s.groups[idx] = payload;
                if (s.selectedGroup.id === payload.id) s.selectedGroup = payload;
                s.isLoading = false;
                s.error = initialState.error;
            })
            .addCase(updateProdGroup.rejected, (s, { payload }) => {
                s.error = { isError: true, message: payload as string };
                s.isLoading = false;
            })

            // delete
            .addCase(deleteProdGroup.pending, (s) => {
                s.isLoading = true;
            })
            .addCase(deleteProdGroup.fulfilled, (s, { payload }) => {
                s.groups = s.groups.filter((g) => g.id !== payload);
                if (s.selectedGroup.id === payload) s.selectedGroup = initialState.selectedGroup;
                s.isLoading = false;
                s.error = initialState.error;
            })
            .addCase(deleteProdGroup.rejected, (s, { payload }) => {
                s.error = { isError: true, message: payload as string };
                s.isLoading = false;
            });
    },
});

export const { selectProdGroup, removeSelectedProdGroup, clearError: clearProdGroupError } = prodGroupSlice.actions;

export default prodGroupSlice.reducer;
